/* PDP in-place image zoom.
 *
 * Replaces the click-to-open lightbox for product images with magnification inside
 * the image's own frame:
 *
 *   - a hover pointer magnifies with the transform origin following the cursor;
 *   - a touch pointer taps to zoom about the tapped point, then drags to pan.
 *
 * Attached to the base-theme gallery from the outside, so no vendor Liquid is forked
 * (AGENTS.md section 3). That works because Horizon's declarative `on:click` binding
 * is a single delegated capture listener on `document` that reads the attribute at
 * dispatch time (assets/component.js) — removing the attribute is therefore enough to
 * retire the lightbox, with no script-ordering race.
 *
 * The `<zoom-dialog>` element and `zoom-dialog.js` are deliberately left in place and
 * untouched. Models are already excluded from the click-to-open path by the gallery
 * snippet, and video slides still open the dialog exactly as before.
 *
 * Magnification is capped by the source's own resolution: `clamp(1.5x, natural /
 * displayed, 3x)`. Shopify never upscales, so a 400px master honestly bottoms out at
 * the floor while a 2000px one gets the full 3x. Most of this catalogue is the former
 * today; nothing here needs changing when higher-resolution images land.
 */
(function () {
  'use strict';

  var SLIDE = '.product-media-container--zoomable.product-media-container--image';
  var IMAGE = 'img.product-media__image';
  var DIALOG = '.dialog-zoomed-gallery';

  var MIN_SCALE = 1.5;
  var MAX_SCALE = 3;
  var TAP_MOVE = 10; // px of travel still counted as a tap, not a swipe
  var TAP_TIME = 500; // ms
  var EASE_MS = 260; // must outlast --animation-speed

  var highResLoaded = new Set();
  var active = null; // the one slide currently magnified

  /* ------------------------------------------------------------------ helpers */

  /** The gallery slides we own: image slides, never the dialog's copies of them. */
  function slidesIn(root) {
    var out = [];
    var galleries = root.querySelectorAll ? root.querySelectorAll('media-gallery') : [];
    if (root.matches && root.matches('media-gallery')) galleries = [root];
    for (var i = 0; i < galleries.length; i++) {
      var found = galleries[i].querySelectorAll(SLIDE);
      for (var j = 0; j < found.length; j++) {
        if (!found[j].closest(DIALOG)) out.push(found[j]);
      }
    }
    return out;
  }

  function slideFor(target) {
    if (!target || !target.closest) return null;
    var slide = target.closest(SLIDE);
    if (!slide || slide.closest(DIALOG)) return null;
    if (!slide.hasAttribute('data-ld-zoom')) return null;
    return slide;
  }

  function imageIn(slide) {
    var img = slide.querySelector(IMAGE);
    return img instanceof HTMLImageElement ? img : null;
  }

  /**
   * Swap in the full-resolution source before magnifying.
   *
   * Ported from `loadHighResolutionImage` in assets/zoom-dialog.js, including its
   * "already loaded" set: the gallery `<img>` already carries `data_max_resolution`
   * (a 3840px request), and Shopify caps that at the master's real width.
   */
  function loadHighRes(slide) {
    var img = imageIn(slide);
    if (!img) return;
    var url = img.getAttribute('data_max_resolution');
    if (!url || highResLoaded.has(url)) return;
    highResLoaded.add(url);

    var next = new Image();
    next.className = img.className;
    next.alt = img.alt;
    next.setAttribute('data_max_resolution', url);
    var ref = img.getAttribute('ref');
    if (ref) next.setAttribute('ref', ref);
    if (img.hasAttribute('style')) next.setAttribute('style', img.getAttribute('style'));
    next.onload = function () {
      if (!img.isConnected) return;
      img.replaceWith(next);
      /* The magnification is derived from the source's real pixels, and until this
       * lands those are a srcset candidate sized for the layout — which always caps
       * at the floor. Recompute so the gesture that triggered the load gets the full
       * range rather than the placeholder value. */
      if (active !== slide || !slide.__ldState) return;
      slide.__ldState.scale = scaleFor(next);
      ease(slide);
      var props = { '--ld-zoom-scale': String(slide.__ldState.scale) };
      if (slide.getAttribute('data-ld-zoom-mode') === 'touch') {
        /* A bigger scale means more room to pan, but the existing offset was clamped
         * against the smaller one — re-clamp so it stays inside the image. */
        var fixed = clamp(slide, next, slide.__ldState.tx, slide.__ldState.ty);
        slide.__ldState.tx = fixed.x;
        slide.__ldState.ty = fixed.y;
        props['--ld-zoom-tx'] = fixed.x.toFixed(1) + 'px';
        props['--ld-zoom-ty'] = fixed.y.toFixed(1) + 'px';
      }
      write(slide, props);
    };
    next.src = url;
  }

  /**
   * How far this particular image can be magnified before it is just upscaling.
   * `offsetWidth` is the untransformed layout width, so this stays correct while a
   * neighbouring slide is already scaled.
   */
  function scaleFor(img) {
    var width = img.offsetWidth;
    if (!width || !img.naturalWidth) return MIN_SCALE;
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, img.naturalWidth / width));
  }

  /**
   * The painted content box inside the `<img>`.
   *
   * The theme forces `object-fit: contain` on gallery images, and paints sub-800px
   * ones at natural size (the `--lowres` rule), so the painted pixels are almost
   * never the same box as the element. Panning has to clamp against the painted box
   * or it scrolls into empty space.
   */
  function paintedBox(img) {
    var bw = img.offsetWidth;
    var bh = img.offsetHeight;
    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    if (!bw || !bh || !nw || !nh) return { width: bw, height: bh };
    var fit = Math.min(bw / nw, bh / nh);
    return { width: nw * fit, height: nh * fit };
  }

  function ease(slide) {
    slide.setAttribute('data-ld-zoom-ease', '');
    if (slide.__ldEase) clearTimeout(slide.__ldEase);
    slide.__ldEase = setTimeout(function () {
      slide.removeAttribute('data-ld-zoom-ease');
      slide.__ldEase = null;
    }, EASE_MS);
  }

  function reset(slide) {
    if (!slide) return;
    if (slide.__ldFrame) {
      cancelAnimationFrame(slide.__ldFrame);
      slide.__ldFrame = null;
    }
    ease(slide);
    slide.setAttribute('data-ld-zoom', 'idle');
    slide.style.removeProperty('--ld-zoom-scale');
    slide.style.removeProperty('--ld-zoom-x');
    slide.style.removeProperty('--ld-zoom-y');
    slide.style.removeProperty('--ld-zoom-tx');
    slide.style.removeProperty('--ld-zoom-ty');
    slide.__ldState = null;
    if (active === slide) active = null;
  }

  function resetAll() {
    reset(active);
  }

  /** Batch every custom-property write into one frame. */
  function write(slide, props) {
    slide.__ldPending = Object.assign(slide.__ldPending || {}, props);
    if (slide.__ldFrame) return;
    slide.__ldFrame = requestAnimationFrame(function () {
      slide.__ldFrame = null;
      var pending = slide.__ldPending;
      slide.__ldPending = null;
      for (var key in pending) slide.style.setProperty(key, pending[key]);
    });
  }

  /* -------------------------------------------------------------- hover mode */

  function hoverEnter(slide, event) {
    if (active && active !== slide) reset(active);
    loadHighRes(slide);
    var img = imageIn(slide);
    if (!img) return;

    /* Measure BEFORE the scale is applied (the write below is deferred to the next
     * frame). Once magnified, the image's own rect is the scaled box and useless for
     * mapping the cursor; the slide is never transformed, so caching the image's
     * offset within it gives a stable frame of reference for every later move. */
    var slideRect = slide.getBoundingClientRect();
    var imgRect = img.getBoundingClientRect();
    if (!imgRect.width || !imgRect.height) return;

    active = slide;
    slide.__ldState = {
      offsetX: imgRect.left - slideRect.left,
      offsetY: imgRect.top - slideRect.top,
      width: imgRect.width,
      height: imgRect.height,
      scale: scaleFor(img),
    };

    slide.setAttribute('data-ld-zoom-mode', 'hover');
    slide.setAttribute('data-ld-zoom', 'active');
    ease(slide);
    write(slide, { '--ld-zoom-scale': String(slide.__ldState.scale) });
    /* Seed the origin from the entry point, so the image magnifies about where the
     * pointer actually is rather than flashing through the centre. */
    if (event) hoverMove(slide, event);
  }

  function hoverMove(slide, event) {
    var state = slide.__ldState;
    if (!state) return;
    var rect = slide.getBoundingClientRect();

    /* Clamped rather than ignored outside the image: a sub-800px source is painted
     * at natural size and centred, so the pointer spends real time in the surrounding
     * gap and the magnifier should sit at the nearest edge, not freeze. */
    var x = ((event.clientX - rect.left - state.offsetX) / state.width) * 100;
    var y = ((event.clientY - rect.top - state.offsetY) / state.height) * 100;
    x = Math.min(100, Math.max(0, x));
    y = Math.min(100, Math.max(0, y));

    write(slide, {
      '--ld-zoom-x': x.toFixed(2) + '%',
      '--ld-zoom-y': y.toFixed(2) + '%',
    });
  }

  /* -------------------------------------------------------------- touch mode */

  function touchZoom(slide, clientX, clientY) {
    loadHighRes(slide);
    var img = imageIn(slide);
    if (!img) return;

    var scale = scaleFor(img);
    active = slide;
    slide.setAttribute('data-ld-zoom-mode', 'touch');
    slide.setAttribute('data-ld-zoom', 'active');
    slide.__ldState = { scale: scale, tx: 0, ty: 0 };
    ease(slide);

    /* Bring the tapped point to the middle of the frame, then clamp it back inside
     * the image. Touch keeps the origin centred so panning is a pure translation. */
    var rect = img.getBoundingClientRect();
    var wanted = clamp(slide, img, (rect.left + rect.width / 2 - clientX) * scale, (rect.top + rect.height / 2 - clientY) * scale);
    slide.__ldState.tx = wanted.x;
    slide.__ldState.ty = wanted.y;

    write(slide, {
      '--ld-zoom-scale': String(scale),
      '--ld-zoom-tx': wanted.x.toFixed(1) + 'px',
      '--ld-zoom-ty': wanted.y.toFixed(1) + 'px',
    });
  }

  /**
   * Clamp a translation so the painted image never pulls away from the frame it is
   * seen through. The transform is `translate() scale()`, i.e. the translation is
   * applied outside the scale, so it is already in screen pixels.
   */
  function clamp(slide, img, x, y) {
    var state = slide.__ldState;
    var scale = state ? state.scale : 1;
    var painted = paintedBox(img);
    var frame = slide.getBoundingClientRect();
    var overflowX = Math.max(0, (painted.width * scale - frame.width) / 2);
    var overflowY = Math.max(0, (painted.height * scale - frame.height) / 2);
    return {
      x: Math.min(overflowX, Math.max(-overflowX, x)),
      y: Math.min(overflowY, Math.max(-overflowY, y)),
    };
  }

  function touchPan(slide, dx, dy) {
    var img = imageIn(slide);
    var state = slide.__ldState;
    if (!img || !state) return;
    var next = clamp(slide, img, state.tx + dx, state.ty + dy);
    state.tx = next.x;
    state.ty = next.y;
    write(slide, {
      '--ld-zoom-tx': next.x.toFixed(1) + 'px',
      '--ld-zoom-ty': next.y.toFixed(1) + 'px',
    });
  }

  /* ------------------------------------------------------------------ wiring */

  /**
   * Retire the lightbox for images and mark the slide as ours.
   *
   * Only image slides in the gallery are touched — the dialog's own `<li>`s carry the
   * same classes plus `on:click="/close"`, and disarming those would break its
   * close-on-tap.
   */
  function enhance(root) {
    var slides = slidesIn(root || document);
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      slide.removeAttribute('on:click');

      /* Grid presentation puts a full-bleed button over the image; it opens the same
       * dialog. Not rendered on this store today (the gallery is set to carousel),
       * but handled so a merchant switching presentation doesn't get the lightbox
       * back. */
      var button = slide.querySelector('.product-media-container__zoom-button');
      if (button) button.removeAttribute('on:click');

      if (slide.getAttribute('data-ld-zoom')) continue;
      slide.setAttribute('data-ld-zoom', 'idle');

      if (!slide.querySelector('.ld-zoom-hint')) {
        var hint = document.createElement('span');
        hint.className = 'ld-zoom-hint';
        hint.setAttribute('aria-hidden', 'true');
        slide.appendChild(hint);
      }
    }
    return slides.length;
  }

  function observe() {
    var galleries = document.querySelectorAll('media-gallery');
    for (var i = 0; i < galleries.length; i++) {
      var gallery = galleries[i];
      if (gallery.__ldObserver) continue;
      /* Horizon re-renders the gallery in place on a variant change, which restores
       * the `on:click` we removed. Re-running on mutation is self-terminating: the
       * removal fires one more mutation that finds nothing left to remove. */
      var observer = new MutationObserver(function () {
        enhance(document);
      });
      observer.observe(gallery, {
        childList: true,
        subtree: true,
        attributeFilter: ['on:click'],
      });
      gallery.__ldObserver = observer;
    }
  }

  function disconnect() {
    var galleries = document.querySelectorAll('media-gallery');
    for (var i = 0; i < galleries.length; i++) {
      if (galleries[i].__ldObserver) {
        galleries[i].__ldObserver.disconnect();
        galleries[i].__ldObserver = null;
      }
    }
    document.removeEventListener('pointerover', onPointerOver);
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerout', onPointerOut);
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('pointerup', onPointerUp, true);
    document.removeEventListener('pointercancel', onPointerCancel, true);
    document.removeEventListener('slideshow:select', onSlideshowSelect);
    document.removeEventListener('variant:update', onVariantUpdate);
    window.removeEventListener('resize', resetAll);
    window.removeEventListener('pagehide', disconnect);
  }

  /* -------------------------------------------------------------- listeners */

  var touch = null; // { slide, id, startX, startY, lastX, lastY, time, moved }

  function onPointerOver(event) {
    if (event.pointerType !== 'mouse') return;
    var slide = slideFor(event.target);
    if (!slide) {
      if (active && active.getAttribute('data-ld-zoom-mode') === 'hover') reset(active);
      return;
    }
    if (slide === active) return;
    /* Leave the carousel's own drag-to-advance alone while it is running. */
    if (slide.closest('slideshow-component[dragging]')) return;
    hoverEnter(slide, event);
  }

  function onPointerMove(event) {
    if (event.pointerType === 'mouse') {
      if (!active || active.getAttribute('data-ld-zoom-mode') !== 'hover') return;
      var slide = slideFor(event.target);
      if (slide !== active) return;
      hoverMove(active, event);
      return;
    }

    if (!touch || event.pointerId !== touch.id) return;
    touch.moved = Math.max(
      touch.moved,
      Math.abs(event.clientX - touch.startX),
      Math.abs(event.clientY - touch.startY)
    );
    if (touch.slide.getAttribute('data-ld-zoom') !== 'active') return;
    event.preventDefault();
    touchPan(touch.slide, event.clientX - touch.lastX, event.clientY - touch.lastY);
    touch.lastX = event.clientX;
    touch.lastY = event.clientY;
  }

  function onPointerOut(event) {
    if (event.pointerType !== 'mouse') return;
    if (!active || active.getAttribute('data-ld-zoom-mode') !== 'hover') return;
    /* `pointerout` also fires moving between children of the same slide. */
    if (event.relatedTarget && slideFor(event.relatedTarget) === active) return;
    reset(active);
  }

  function onPointerDown(event) {
    if (event.pointerType === 'mouse') return;
    var slide = slideFor(event.target);
    if (!slide) {
      if (active) reset(active);
      return;
    }
    if (active && active !== slide) reset(active);
    loadHighRes(slide);
    touch = {
      slide: slide,
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      time: Date.now(),
      moved: 0,
    };
    if (slide.getAttribute('data-ld-zoom') === 'active' && slide.setPointerCapture) {
      slide.setPointerCapture(event.pointerId);
    }
  }

  function onPointerUp(event) {
    if (!touch || event.pointerId !== touch.id) return;
    var slide = touch.slide;
    var isTap = touch.moved <= TAP_MOVE && Date.now() - touch.time <= TAP_TIME;
    touch = null;

    if (!isTap) return;
    if (slide.getAttribute('data-ld-zoom') === 'active') {
      reset(slide);
    } else {
      touchZoom(slide, event.clientX, event.clientY);
    }
  }

  function onPointerCancel(event) {
    if (touch && event.pointerId === touch.id) touch = null;
  }

  /**
   * Reset when the carousel moves to a different slide — but only then.
   *
   * `slideshow.js` also runs its pointer-up handler on every `pointerdown` (its own
   * guard against a stray drag in the zoom dialog), and that dispatches a select
   * event for the slide already showing. Treating that as a slide change cancelled
   * the zoom the moment a pan started.
   */
  function onSlideshowSelect(event) {
    if (!active) return;
    var slide = event.detail && event.detail.slide;
    if (slide && slide === active) return;
    reset(active);
  }

  function onVariantUpdate() {
    resetAll();
    /* The morph lands after the event; re-arm on the next frame as well as via the
     * MutationObserver, so a slow morph cannot leave a slide un-enhanced. */
    requestAnimationFrame(function () {
      enhance(document);
    });
  }

  function init() {
    if (!document.querySelector('media-gallery')) return;
    enhance(document);
    observe();

    document.addEventListener('pointerover', onPointerOver);
    document.addEventListener('pointermove', onPointerMove, { passive: false });
    document.addEventListener('pointerout', onPointerOut);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointerup', onPointerUp, true);
    document.addEventListener('pointercancel', onPointerCancel, true);
    document.addEventListener('slideshow:select', onSlideshowSelect);
    document.addEventListener('variant:update', onVariantUpdate);
    window.addEventListener('resize', resetAll);
    window.addEventListener('pagehide', disconnect);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
